"use client";

import { motion } from "framer-motion";
import { cn } from "@/lib/cn";
import { EASE } from "@/lib/motion";
import { PlatformError } from "@/lib/api/client";
import { messageFor } from "@/lib/api/messages";
import { missionTarget } from "@/lib/api/mission-progress";
import { useMissions } from "@/lib/api/missions";
import { useContent } from "@/components/shell/ContentContext";
import { Icon } from "@/components/ui/Icon";
import { Chip, Panel, PanelHeader, ProgressBar } from "@/components/ui/primitives";
import { SignedOut } from "@/components/ui/SignedOut";
import { SectionEmpty, SectionError } from "@/components/ui/states";

export function MissionBoard() {
  const missions = useContent().missions;
  const { states, isSignedIn, isError, error, completed } = useMissions(missions);
  const listed = states.filter((state) => missionTarget(state.mission) !== "");

  return (
    <Panel>
      <PanelHeader
        icon="crossed"
        title="Missions"
        action={
          isSignedIn && listed.length > 0 ? (
            <Chip tone="brand">
              {completed} / {listed.length} complete
            </Chip>
          ) : undefined
        }
      />

      {listed.length === 0 && <SectionEmpty icon="crossed" title="No missions are running" />}

      {!isSignedIn && listed.length > 0 && (
        <SignedOut
          title="Progress needs a session"
          body="What each mission asks for is below. How far into it you are is measured from your own account, which the platform only returns once you are signed in."
        />
      )}

      {isSignedIn && isError && (
        <SectionError
          title="Your progress didn’t load"
          body={messageFor(error as PlatformError, "account")}
        />
      )}

      {listed.length > 0 && (
        <div className="grid gap-3 px-6 pb-6 lg:grid-cols-2">
          {listed.map((state, i) => {
            const complete = Boolean(state.progress?.complete);
            const percent = Math.round(state.progress?.percent ?? 0);

            return (
              <motion.article
                key={state.mission.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, ease: EASE, delay: i * 0.07 }}
                className="flex flex-col justify-between gap-4 rounded-lg bg-elevation-2 p-4 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="flex gap-3">
                  <span
                    className={cn(
                      "grid h-[44px] w-[44px] shrink-0 place-items-center rounded-md",
                      complete ? "bg-brand text-fg-on-primary" : "bg-brand/18 text-brand-bold",
                    )}
                  >
                    <Icon name={complete ? "check" : "crossed"} size={20} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-[14px] font-bold leading-[1.6]">{state.mission.title}</h3>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <Chip className="bg-elevation-3 capitalize">{state.mission.metric}</Chip>
                    </div>
                  </div>
                </div>

                <div>
                  <ProgressBar value={percent} height={22} striped />
                  <p className="mt-1.5 text-[12px] font-bold leading-[1.6] text-fg-subtle">
                    {state.progress
                      ? state.progress.remaining
                      : `Target: ${missionTarget(state.mission)}`}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
